// src/features/rooms/RoomsTable.tsx
import "ag-grid-enterprise";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Center, Loader, Box, TextInput, Stack, Badge } from "@mantine/core";
import { AgGridReact } from "ag-grid-react";
import type { CellFocusedEvent, ColDef, ICellRendererParams } from "ag-grid-enterprise";
import type { IRoomSummary } from "@backend/src/schemas";

import { getPercentColor } from "../../DesignTab/LeftSidebar/ColorPanel/colorPanelUtils";
import { useProjectId } from "../../hooks/useProjectId";
import { usePostMultiplacerRoomsQuery } from "../../../openapi";
import { selectValidationColumnMetas } from "./validationColumnMetas";
import { buildValidationColumns } from "./buildValidationColumns";
import { useAgGridReduxState } from "../../hooks/useAgGridReduxState";
import { selectAgGridStateByKey } from "../../agGridStateSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setSelectedSpaceNumberInRedux } from "../../DesignTab/designSlice";
import type { IValidationColumnMeta } from "./validationColumnMetas";
import {
  DR_STATUS_COLORS,
  MMI_COLORS,
} from "../../DesignTab/LeftSidebar/ColorPanel/colorPanelUtils";
import { ColoredBadgeCellRenderer } from "./ColoredBadgeCellRenderer";

/** Formaterar arrayer kort */
function fmtArrShort(v?: number[], decimals = 3) {
  if (!Array.isArray(v)) return "";
  return v.map((n) => Number(n).toFixed(decimals)).join(", ");
}

// Status-badge renderer (brukes av dynamiske statuskolonner)
const ValidationStatusBadgeCellRenderer: React.FC<
  ICellRendererParams<IRoomSummary, unknown> & { meta?: IValidationColumnMeta }
> = (params) => {
  const meta = params.meta;

  const label = meta?.valueFormatter
    ? meta.valueFormatter(params.value)
    : params.value == null
      ? "–"
      : String(params.value);

  const fill = meta?.valueToColor?.(params.value);

  // No value -> "Ingen status"
  if (params.value == null || params.value === "") {
    return (
      <Badge size="sm" variant="light" color="gray" style={{ textTransform: "none" }}>
        {label}
      </Badge>
    );
  }

  return (
    <Badge
      size="sm"
      variant="filled"
      style={{
        backgroundColor: fill ?? "#868e96",
        color: "#ffffff",
        textTransform: "none",
      }}
    >
      {label}
    </Badge>
  );
};

/** Procent-renderer med färg */
const PercentCellRenderer: React.FC<
  ICellRendererParams<IRoomSummary, number | undefined> & {
    getValue?: (row: IRoomSummary) => number | undefined;
  }
> = (params) => {
  const raw =
    typeof params.value === "number"
      ? params.value
      : typeof params.getValue === "function"
        ? params.getValue(params.data as IRoomSummary)
        : undefined;

  const percent = typeof raw === "number" && Number.isFinite(raw) ? Math.round(raw) : undefined;
  const bg = typeof percent === "number" ? getPercentColor(percent) : undefined;
  const label = typeof percent === "number" ? `${percent} %` : "—";

  const style: React.CSSProperties = {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontVariantNumeric: "tabular-nums",
    borderRadius: 6,
    backgroundColor: bg,
    color: "#111",
    fontWeight: 500,
  };

  return (
    <div style={style} title={label}>
      {label}
    </div>
  );
};

/** Klickbar rumsnummer-länk */
const RoomNumberLinkRenderer: React.FC<ICellRendererParams<IRoomSummary>> = (params) => {
  const dispatch = useAppDispatch();
  const roomNumber = params.value as string;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(setSelectedSpaceNumberInRedux(roomNumber));
  };

  return (
    <a
      href="#"
      onClick={handleClick}
      style={{
        color: "#0066cc",
        textDecoration: "none",
        cursor: "pointer",
        fontWeight: 500,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
      onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
    >
      {roomNumber}
    </a>
  );
};

export const RoomsTable: React.FC = () => {
  const projectId = useProjectId();
  const dispatch = useAppDispatch();
  const selectedSpaceNumber = useAppSelector((s) => s.design.selectedSpaceNumber);

  const {
    data: rooms = {},
    isLoading,
    isError,
  } = usePostMultiplacerRoomsQuery({
    body: { projectNo: projectId },
  });

  const rows: IRoomSummary[] = useMemo(() => Object.values(rooms ?? {}) as IRoomSummary[], [rooms]);

  const mmiColorMap = useMemo<Record<string, string>>(
    () => Object.fromEntries(Object.entries(MMI_COLORS).map(([k, v]) => [String(k), v])),
    []
  );

  const baseColumnDefs = useMemo<ColDef<IRoomSummary>[]>(
    () => [
      // KLICKBART RUMSNUMMER
      {
        colId: "roomNumber",
        headerName: "Rom",
        field: "roomNumber",
        sortable: true,
        filter: true,
        minWidth: 160,
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: RoomNumberLinkRenderer,
        },
      },
      {
        colId: "roomName",
        headerName: "Navn",
        field: "roomName",
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        colId: "drStatus",
        headerName: "Dr status",
        field: "drStatus",
        sortable: true,
        filter: true,
        width: 140,
        cellRenderer: ColoredBadgeCellRenderer,
        cellRendererParams: {
          colorMap: DR_STATUS_COLORS,
          titlePrefix: "dR-status",
          toKey: (v: unknown) => (typeof v === "string" ? v.trim() : ""),
        },
      },
      {
        colId: "rfpStatus",
        headerName: "RFP-status",
        field: "rfpStatus",
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        colId: "rfpMalRom",
        headerName: "Standardrom",
        field: "rfpMalRom",
        sortable: true,
        filter: true,
        width: 140,
      },
      {
        colId: "supplyAirFlow",
        headerName: "Tilluft (m³/h)",
        field: "supplyAirFlow",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 140,
      },
      {
        colId: "returnAirFlow",
        headerName: "Avtrekk (m³/h)",
        field: "returnAirFlow",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 150,
      },
      {
        colId: "ceilingCoordinate",
        headerName: "Himlingskoordinat (x, y, z)",
        field: "ceilingCoordinate",
        valueFormatter: (p) => fmtArrShort(p.value, 3),
        tooltipValueGetter: (p) =>
          Array.isArray(p.data?.ceilingCoordinate) ? p.data!.ceilingCoordinate.join(", ") : "",
        flex: 1.2,
        hide: true,
      },
      {
        colId: "ceilingDirection",
        headerName: "Himlingsretning (x, y)",
        field: "ceilingDirection",
        valueFormatter: (p) => fmtArrShort(p.value, 3),
        tooltipValueGetter: (p) =>
          Array.isArray(p.data?.ceilingDirection) ? p.data!.ceilingDirection.join(", ") : "",
        flex: 1.0,
      },
      {
        colId: "extraIfcInfo.pns",
        headerName: "PNS",
        valueGetter: (p) => p.data?.extraIfcInfo?.pns ?? null,
        width: 140,
        sortable: true,
        filter: true,
      },
      {
        colId: "extraIfcInfo.mmi",
        headerName: "MMI",
        valueGetter: (p) => p.data?.extraIfcInfo?.mmi ?? null,
        width: 140,
        sortable: true,
        filter: true,
        cellRenderer: ColoredBadgeCellRenderer,
        cellRendererParams: {
          colorMap: mmiColorMap,
          titlePrefix: "MMI",
          toKey: (v: unknown) => {
            if (typeof v === "number" && Number.isFinite(v)) return String(v);
            if (typeof v === "string") return v.trim();
            return "";
          },
        },
      },
      {
        colId: "extraIfcInfo.floor",
        headerName: "Etasje",
        valueGetter: (p) => p.data?.extraIfcInfo?.floor ?? null,
        width: 120,
        sortable: true,
        filter: true,
      },
    ],
    [mmiColorMap]
  );

  const validationMetas = useSelector(selectValidationColumnMetas);
  const validationColDefs = useMemo<ColDef<IRoomSummary>[]>(() => {
    return buildValidationColumns(validationMetas, {
      percentCellRenderer: "PercentCellRenderer",
      statusCellRenderer: "ValidationStatusBadgeCellRenderer",
    });
  }, [validationMetas]);

  const columnDefsWithGroups = useMemo<ColDef<IRoomSummary>[]>(() => {
    const roomCols = baseColumnDefs.filter((c) =>
      ["roomNumber", "roomName"].includes(String(c.colId))
    );

    const rfpCols = baseColumnDefs.filter((c) =>
      ["rfpStatus", "rfpMalRom", "drStatus"].includes(String(c.colId))
    );

    const luftCols = baseColumnDefs.filter((c) =>
      ["supplyAirFlow", "returnAirFlow"].includes(String(c.colId))
    );

    const revitCols = baseColumnDefs.filter((c) =>
      ["ceilingCoordinate", "ceilingDirection"].includes(String(c.colId))
    );

    const ifcCols = baseColumnDefs.filter((c) =>
      ["extraIfcInfo.pns", "extraIfcInfo.mmi", "extraIfcInfo.floor"].includes(String(c.colId))
    );

    return [
      { headerName: "Romdata", children: roomCols },
      { headerName: "RFP", children: rfpCols },
      { headerName: "Luftmengder", children: luftCols },
      { headerName: "Revit", children: revitCols },
      { headerName: "IFC", children: ifcCols },
      { headerName: "RFP standardrom valideringer", children: validationColDefs },
    ];
  }, [baseColumnDefs, validationColDefs]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      flex: undefined,
      enableRowGroup: true,
      enablePivot: true,
      enableValue: true,
    }),
    []
  );

  const allColIds = useMemo(() => {
    const base = baseColumnDefs.map((c) => c.colId!).filter(Boolean);
    const dyn = validationColDefs.map((c) => c.colId!).filter(Boolean);
    return [...base, ...dyn];
  }, [baseColumnDefs, validationColDefs]);

  const columnsSignature = useMemo(() => allColIds.join("|"), [allColIds]);
  const gridKey = `roomsTable:${projectId}`;
  const saved = useSelector(selectAgGridStateByKey(gridKey));
  const hasSavedCols = !!(saved?.columnState && saved.columnState.length > 0);
  const [search, setSearch] = useState(saved?.quickFilter ?? "");

  useEffect(() => {
    if (saved?.quickFilter != null) setSearch(saved.quickFilter);
  }, [saved?.quickFilter]);

  const { gridStateHandlers, setQuickFilterText } = useAgGridReduxState(gridKey, {
    availableColIds: allColIds,
    columnsSignature,
    managePivotMode: false,
  });

  if (isLoading)
    return (
      <Center h="100%">
        <Loader />
      </Center>
    );
  if (isError) return <div>Kunne ikke laste rom.</div>;

  return (
    <Stack h="100%" w="100%" gap="sm">
      <TextInput
        style={{ width: 300 }}
        placeholder="Søk i alle kolonner…"
        value={search}
        onChange={(e) => {
          const v = e.currentTarget.value;
          setSearch(v);
          setQuickFilterText(v);
        }}
      />

      <Box className="ag-theme-quartz" style={{ width: "100%", minHeight: 300, flex: 1 }}>
        <AgGridReact<IRoomSummary>
          rowData={rows}
          columnDefs={columnDefsWithGroups}
          defaultColDef={defaultColDef}
          animateRows
          tooltipShowDelay={150}
          autoSizeStrategy={
            hasSavedCols ? undefined : { type: "fitGridWidth", defaultMinWidth: 110 }
          }
          overlayNoRowsTemplate="<span style='padding:8px;display:inline-block;'>Ingen rom funnet</span>"
          groupHeaderHeight={36}
          headerHeight={56}
          suppressScrollOnNewData
          sideBar={["columns"]}
          components={{
            PercentCellRenderer,
            RoomNumberLinkRenderer,
            ValidationStatusBadgeCellRenderer,
            ColoredBadgeCellRenderer,
          }}
          {...gridStateHandlers}
          getRowStyle={(params) => {
            if (params.data?.roomNumber === selectedSpaceNumber) {
              return {
                background: "rgba(0, 102, 204, 0.12)",
                fontWeight: 600,
                borderLeft: "4px solid #0066cc",
                boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)",
              };
            }
            return undefined;
          }}
          onCellFocused={(event: CellFocusedEvent) => {
            if (event.rowIndex == null) return;

            const rowNode = event.api.getDisplayedRowAtIndex(event.rowIndex);
            const data = rowNode?.data as IRoomSummary | undefined;

            if (data?.roomNumber) {
              dispatch(setSelectedSpaceNumberInRedux(data.roomNumber));
            }
          }}
        />
      </Box>
    </Stack>
  );
};
